import * as z from "zod";

import { getValueAtPath } from "./get-value-at-path";

type Locale = Record<string, unknown>;

type LocaleOptionsDefault = string;
type LocaleOptionsKeys = Record<string, string>;
export type LocaleOptions =
  | LocaleOptionsDefault
  | LocaleOptionsKeys
  | {
      defaultValue: LocaleOptionsDefault;
      keys: LocaleOptionsKeys;
    };

const valueAtPathSchema = z
  .union([z.string(), z.tuple([z.string(), z.record(z.string(), z.string())])])
  .optional();

export function getValueAtPathFromLocale(
  locale: Locale,
  path: string,
  options: LocaleOptions = {},
): string {
  let defaultValue: string | undefined;
  if (typeof options === "string") {
    defaultValue = options;
  } else if (typeof options === "object" && "defaultValue" in options) {
    defaultValue = options.defaultValue;
  }

  let keys: Record<string, string> | undefined;
  if (typeof options === "object") {
    if ("keys" in options && typeof options.keys === "object") {
      keys = options.keys;
    } else if (
      typeof options === "object" &&
      !("keys" in options) &&
      !("defaultValue" in options)
    ) {
      keys = options;
    }
  }

  return getLocaleValue(locale, path, {
    ...(defaultValue && { defaultValue }),
    ...(keys && { keys }),
  });
}

function getLocaleValue(
  locale: Record<string, unknown>,
  path: string,
  {
    defaultValue,
    previousPaths = [],
    keys = {},
  }: {
    previousPaths?: string[];
    defaultValue?: LocaleOptionsDefault;
    keys?: LocaleOptionsKeys;
  } = {},
): string {
  const valueAtPath = valueAtPathSchema.parse(getValueAtPath(locale, path));
  if (!valueAtPath) {
    if (defaultValue) {
      return defaultValue;
    }
    return path;
  }
  let value = typeof valueAtPath === "string" ? valueAtPath : valueAtPath[0];

  // Get all keys from the value and append to provided keys
  if (valueAtPath instanceof Array) {
    value = valueAtPath[0];
    for (const [key, value] of Object.entries(valueAtPath[1])) {
      keys = {
        ...keys,
        [key]: value,
      };
    }
  }

  const matchKeys = value.match(/{[a-zA-Z0-9]+}/g);
  if (matchKeys !== null) {
    for (const key of Object.keys(keys)) {
      value = value.replace(new RegExp(`{${key}}`, "g"), keys[key] ?? "");
    }
  }

  const matchPlaceholders = value.match(
    /:[a-zA-Z0-9.]+(\{[a-zA-Z0-9.:,]+\})?(\[[a-zA-Z0-9]+\])?/g,
  );
  if (matchPlaceholders !== null) {
    if (previousPaths.includes(path)) {
      throw new Error(`Circular reference detected for path: ${path}`);
    }
    previousPaths = [...previousPaths, path];
    for (const placeholder of matchPlaceholders) {
      const matchPlaceholder = placeholder.match(
        /(:[a-zA-Z0-9.]+)(?:\{([a-zA-Z0-9.:,]+)\})?(?:\[([a-zA-Z0-9]+)\])?/,
      );
      const allMatches = matchPlaceholder?.[0];
      const placeholderValue = matchPlaceholder?.[1];
      const placeholderModifier = matchPlaceholder?.[3];

      if (!allMatches || !placeholderValue) {
        continue;
      }

      let keyValue = getLocaleValue(locale, placeholderValue.slice(1), {
        previousPaths,
        keys,
      });

      if (placeholderModifier) {
        keyValue = applyModifier(keyValue, placeholderModifier);
      }
      value = value.replace(allMatches, keyValue);
    }
  }

  return value;
}

function applyModifier(value: string, modifier: string): string {
  switch (modifier) {
    case "lowercase":
      return value.toLowerCase();
    case "uppercase":
      return value.toUpperCase();
    case "capitalize":
      return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
    default:
      return value;
  }
}
